import * as React from 'react';
import {
  Star,
  Clock,
  Building2,
  ShoppingBag,
  Calendar,
  ChevronRight,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface SellerProfileCardProps {
  /** Display name */
  name: string;
  /** Avatar URL (optional, falls back to initials) */
  avatarUrl?: string;
  /** Star rating, 0-5 */
  rating: number;
  /** Total number of reviews */
  reviewCount: number;
  /** ISO date string of when the user joined */
  memberSince: string;
  /** Condominium name */
  condominiumName: string;
  /** Whether seller is in the same building as the viewer */
  isSameBuilding?: boolean;
  /** Average response time, human-readable (e.g. "< 1 hora") */
  responseTime: string;
  /** Total items sold */
  itemsSold: number;
  /** Navigate to full profile */
  onViewProfile?: () => void;
  /** Compact variant for embedding inside product detail */
  compact?: boolean;
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatMemberSince(iso: string): string {
  const date = new Date(iso);
  const month = date.toLocaleString('es-CL', { month: 'short' });
  const year = date.getFullYear();
  // Capitalize first letter
  return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${year}`;
}

/* ------------------------------------------------------------------ */
/*  Star display                                                       */
/* ------------------------------------------------------------------ */

function StarRating({
  rating,
  count,
  size = 'md',
}: {
  rating: number;
  count: number;
  size?: 'sm' | 'md';
}) {
  const starSize = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const textSize = size === 'sm' ? 'text-[0.6875rem]' : 'text-xs';

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5" role="img" aria-label={`${rating} de 5 estrellas`}>
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={i}
            className={cn(
              starSize,
              i < Math.round(rating)
                ? 'fill-[var(--color-ochre)] text-[var(--color-ochre)]'
                : 'fill-none text-[var(--color-hairline-strong)]',
            )}
          />
        ))}
      </div>
      <span className={cn(textSize, 'font-semibold text-[var(--color-ink)]')}>
        {rating.toFixed(1)}
      </span>
      <span className={cn(textSize, 'text-[var(--color-ink-faint)]')}>
        ({count})
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Stat pill                                                          */
/* ------------------------------------------------------------------ */

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2 text-[0.8125rem]">
      <Icon className="h-3.5 w-3.5 shrink-0 text-[var(--color-ink-faint)]" />
      <span className="text-[var(--color-ink-muted)]">{label}</span>
      <span className="ml-auto font-medium text-[var(--color-ink)]">{value}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * SellerProfileCard
 * -----------------
 * Mini profile card showing seller reputation and metadata.
 * Two variants:
 *   - Default: full card with border, padding, and all stats
 *   - Compact: inline row for embedding in product detail
 *
 * Visual treatment:
 * - Paper background with hairline border (like KPI cards)
 * - Avatar with ring, initials fallback
 * - Ochre stars for rating (matches the existing ochre token)
 * - Stats use icon + label + value layout
 * - "Tu edificio" badge uses primary bg (same as ProductCard)
 * - Hover: subtle elevation lift on the entire card
 */
export function SellerProfileCard({
  name,
  avatarUrl: _avatarUrl,
  rating,
  reviewCount,
  memberSince,
  condominiumName,
  isSameBuilding = false,
  responseTime,
  itemsSold,
  onViewProfile,
  compact = false,
  className,
}: SellerProfileCardProps) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();

  /* ---- Compact variant ---- */
  if (compact) {
    return (
      <button
        type="button"
        onClick={onViewProfile}
        className={cn(
          'flex w-full items-center gap-3 rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] px-4 py-3',
          'transition-all duration-150 hover:border-[var(--color-hairline-strong)] hover:bg-[var(--color-paper-dim)]',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
          className,
        )}
      >
        <Avatar className="h-10 w-10">
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1 text-left">
          <p className="text-sm font-semibold text-[var(--color-ink)]">{name}</p>
          <div className="mt-0.5 flex items-center gap-2">
            <StarRating rating={rating} count={reviewCount} size="sm" />
            {isSameBuilding && (
              <Badge variant="primary" className="text-[0.5625rem] py-0">
                Tu edificio
              </Badge>
            )}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-[var(--color-ink-faint)]" />
      </button>
    );
  }

  /* ---- Full variant ---- */
  return (
    <div
      className={cn(
        'rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)]',
        'transition-all duration-200 hover:border-[var(--color-hairline-strong)]',
        'hover:shadow-[0_4px_20px_-6px_rgba(12,30,40,0.08)]',
        className,
      )}
    >
      {/* Header with avatar */}
      <div className="flex items-start gap-4 p-5">
        <Avatar className="h-14 w-14 text-lg">
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-lg font-semibold tracking-tight text-[var(--color-ink)]">
              {name}
            </h3>
            {isSameBuilding && (
              <Badge variant="primary" className="text-[0.5625rem]">
                <Building2 className="h-2.5 w-2.5" />
                Tu edificio
              </Badge>
            )}
          </div>
          <div className="mt-1">
            <StarRating rating={rating} count={reviewCount} />
          </div>
          <p className="mt-1 text-[0.8125rem] text-[var(--color-ink-muted)]">
            {condominiumName}
          </p>
        </div>
      </div>

      {/* Divider */}
      <div className="mx-5 h-px bg-[var(--color-hairline)]" />

      {/* Stats */}
      <div className="space-y-3 p-5">
        <Stat
          icon={Calendar}
          label="Miembro desde"
          value={formatMemberSince(memberSince)}
        />
        <Stat
          icon={Clock}
          label="Tiempo de respuesta"
          value={responseTime}
        />
        <Stat
          icon={ShoppingBag}
          label="Articulos vendidos"
          value={String(itemsSold)}
        />
      </div>

      {/* View profile CTA */}
      {onViewProfile && (
        <div className="border-t border-[var(--color-hairline)] px-5 py-3">
          <button
            type="button"
            onClick={onViewProfile}
            className="flex w-full items-center justify-center gap-1 text-sm font-medium text-[var(--color-primary)] transition-colors hover:text-[var(--color-primary-600)]"
          >
            Ver perfil completo
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Skeleton                                                           */
/* ------------------------------------------------------------------ */

export function SellerProfileCardSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)]',
        className,
      )}
    >
      <div className="flex items-start gap-4 p-5">
        <Skeleton className="h-14 w-14 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-40" />
        </div>
      </div>
      <div className="mx-5 h-px bg-[var(--color-hairline)]" />
      <div className="space-y-3 p-5">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
      </div>
    </div>
  );
}
